import { useArrived, useTooltip } from './Tooltip'

// Tier by band: the register's heat grid, every count a link to the vendors in
// that cell. A filled cell carries its band's colour with the ink chosen for
// that step, and an empty one stays quiet on the sunk surface. The cells arrive
// with a short stagger, the way the proposal draws them.

export const BAND_COLOR: Record<string, string> = {
  LOW: 'var(--hc-b-low)',
  MEDIUM: 'var(--hc-b-med)',
  HIGH: 'var(--hc-b-high)',
  CRITICAL: 'var(--hc-b-crit)',
  INSUFFICIENT_EVIDENCE: 'var(--hc-b-ins)',
  NONE: 'var(--hc-b-none)',
}
const BAND_INK: Record<string, string> = {
  LOW: 'var(--hc-b-low-ink)',
  MEDIUM: 'var(--hc-b-med-ink)',
  HIGH: 'var(--hc-b-high-ink)',
  CRITICAL: 'var(--hc-b-crit-ink)',
  INSUFFICIENT_EVIDENCE: 'var(--hc-b-ins-ink)',
}
const BAND_LONG: Record<string, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
  INSUFFICIENT_EVIDENCE: 'Insufficient evidence',
  NONE: 'Not scored',
}

export type TierRow = { tier: number; label: string; counts: number[]; hrefs: string[] }

function shortTier(label: string): string {
  // "Tier 1 Critical" reads as "Tier 1" in a 70px column; the full words are
  // the row's title and its accessible name.
  const m = /^(Tier \d)/.exec(label)
  return m?.[1] ?? label
}

export function TierGrid({ bands, short, rows }: { bands: string[]; short: string[]; rows: TierRow[] }) {
  const arrived = useArrived()
  const tip = useTooltip()
  let k = 0
  return (
    <div className={'hc-heat' + (arrived ? ' on' : '')} role="table" aria-label="Vendors by tier and risk band">
      <span role="presentation" />
      {bands.map((b, i) => (
        <span key={b} className="h" role="columnheader" title={BAND_LONG[b]}>
          {short[i]}
        </span>
      ))}
      {rows.map((row) => (
        <Row key={row.tier} row={row} bands={bands} short={short} stagger={() => k++} tip={tip} />
      ))}
    </div>
  )
}

function Row({
  row,
  bands,
  short,
  stagger,
  tip,
}: {
  row: TierRow
  bands: string[]
  short: string[]
  stagger: () => number
  tip: ReturnType<typeof useTooltip>
}) {
  return (
    <>
      <span className="r" role="rowheader" title={row.label}>
        {shortTier(row.label)}
      </span>
      {bands.map((b, i) => {
        const n = row.counts[i] ?? 0
        const href = row.hrefs[i]
        const filled = n > 0 && b !== 'NONE'
        const style = {
          transitionDelay: stagger() * 18 + 'ms',
          ...(filled ? { background: BAND_COLOR[b], color: BAND_INK[b] } : {}),
        }
        const name = row.label + ', ' + (BAND_LONG[b] ?? b) + ': ' + n
        const rows = [{ label: BAND_LONG[b] ?? short[i] ?? b, value: String(n), color: BAND_COLOR[b] }]
        const events = {
          onPointerMove: (e: React.PointerEvent) => tip.show(e, row.label, rows),
          onPointerLeave: () => tip.hide(),
          onFocus: (e: React.FocusEvent) => tip.showAt(e.currentTarget, row.label, rows),
          onBlur: () => tip.hide(),
        }
        return n > 0 && href ? (
          <a key={b} className="cell hc-num" role="cell" href={href} style={style} aria-label={name} {...events}>
            {n}
          </a>
        ) : (
          <span key={b} className="cell hc-num" role="cell" style={style} aria-label={name} {...events}>
            {n}
          </span>
        )
      })}
    </>
  )
}
